import React from 'react'
import config from '@adc/configment'
// @ts-ignore no types
import _ from 'lodash'
import { serializeParams } from '@adc/fetch'
import type { AuctionBlueprintOnlineReactiveArgs } from '@adc/blueprint'
import classnames from 'classnames/bind'
// @ts-ignore no types
import { SimpleCard, Button, Icon, Text } from '@adc/ui-adc'
import analytics from '../../services/analytics'
// @ts-ignore no types
import styles from './index.scss'

const cx = classnames.bind(styles)
type Listing = AuctionBlueprintOnlineReactiveArgs['listing']

/**
 * Grabs the URL to the sample title report from listing's global docs
 */
export const getSampleTitleReport = (listing: Listing) =>
  listing?.applicable_global_documents?.find(
    doc => doc?.media_purpose_type_code === 'SAMPLE_THIRD_PARTY_TITLE_REPORT'
  )

/**
 * Builds the params based on the seller address info
 */
const constructAddressParams = (listing: Listing) => {
  const addressInfo = listing?.seller_property

  if (!addressInfo) return null

  const params = {
    PropertyAddress: addressInfo.street_description || '',
    PropertyCity: addressInfo.municipality || '',
    PropertyState: addressInfo.country_primary_subdivision || '',
    PropertyZip: addressInfo.postal_code || '',
  }
  return serializeParams(params)
}

interface BuildExternalLinkParams {
  listing: Listing
  providerConfig: {
    LINK_BASE_URL: string
    LINK_TITLE: string
    LINK_SUBTEXT: string
  }
}

/**
 * Builds the external redirect link to the title provider
 */
export const buildExternalLink = ({
  listing,
  providerConfig,
}: BuildExternalLinkParams) => {
  const {
    LINK_BASE_URL: linkBaseUrl,
    LINK_TITLE: linkTitle,
    LINK_SUBTEXT: linkSubText,
  } = providerConfig
  const redirectTitle = encodeURIComponent(linkTitle)
  const redirectSubText = linkSubText ? encodeURIComponent(linkSubText) : ''
  const params = constructAddressParams(listing)

  if (!params) return null

  /**
   * Note - there is some intentional double-encoding happening here once with the
   * serialize and once with the encodeURIComponent.  This is because we want to
   * (a) encode the URL going into /external-redirect and after decode/parsing we
   * want to (b) encode the `url` as it's redirected the external site.
   */
  const redirectUrl = encodeURIComponent(`${linkBaseUrl}?${params}`)
  return `/external-redirect?url=${redirectUrl}&title=${redirectTitle}${
    redirectSubText ? '&subtext=' + redirectSubText : ''
  }`
}

export const PurchaseTitleReport = ({ listing }: { listing: Listing }) => {
  const thirdPartyProviders = config.get('TITLE_REPORT_PROVIDERS', {})

  const providerConfig = _.get(
    thirdPartyProviders,
    String(listing?.program_configuration?.third_party_title_provider),
    null
  )
  if (!providerConfig) return null // only display card if we have a corresponding config in SSP.

  const link = buildExternalLink({ listing, providerConfig })
  if (!link) return null // only display card if we have the info to build the proper link.

  const sampleDocumentUrl = getSampleTitleReport(listing)?.media_download_uri

  const reportAndOpenLink = () => {
    analytics.purchaseTitleReport()
    window.open(link, '_blank')
  }

  return (
    <SimpleCard
      className={cx('purchase-title-report')}
      elmId='auction_additional_details'
      title='Purchase Title Report'
      titleIcon={
        <Icon name='file' color='white' size='big' iconStyle='light' fitted />
      }
      content={
        <>
          <Text className='u-mb-3' color='grayDark'>
            Purchase your next home with confidence. See property history, lien
            information, and more.{' '}
            {sampleDocumentUrl && (
              <a
                data-elm-id='download_url'
                onClick={() => window.open(sampleDocumentUrl, '_blank')}
              >
                Download Sample.
              </a>
            )}
          </Text>
          <Text color='gray' size='mini' className='u-mb-2'>
            Link to external site. No affiliation with Auction.com.
          </Text>
          <Button
            elmId='purchase_title_report_url'
            size='small'
            role='default'
            fluid
            onClick={reportAndOpenLink}
          >
            Purchase Title Report
          </Button>
        </>
      }
    />
  )
}
